import { useState } from "react";
import { updateCustomisationValue } from "../components/v2/CustomisationWorkspace/WorkspaceComponents/EditablePreview/EditablePreview";
import { triggerSaveFeatureData } from "../components/v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";

export const ACTION_TYPES = {
    CUSTOMISATION_VALUE_CHANGE: 'customisation_value_change',
    CUSTOMISATION_VALUE_DELETED: 'customisation_value_deleted',
    CUSTOMISATION_VALUE_ADDED: 'customisation_value_added',
    CUSTOMISATION_VALUES_ARRAY_REPLACED: 'customisation_values_array_replaced',
}

export const useUndoSystem = (onUndoRedo) => {
    // Undo/Redo stacks
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    // Function to record an action
    const recordAction = (action) => {
        console.log('Recording action: ', action);
        setUndoStack((prevStack) => [...prevStack, action]);
        setRedoStack([]); // Clear redo stack on new action
    };

    const recordCustomisationValueChange = (featureId, node, dataLink, oldValue, newValue, index, visualisationComponentKey, visualisationExtraData) => {
        if (!featureId || !node || !dataLink || oldValue === newValue) return;
        const action = {
            type: ACTION_TYPES.CUSTOMISATION_VALUE_CHANGE,
            payload: { featureId, node, dataLink, oldValue, newValue, index, visualisationComponentKey, visualisationExtraData}
        };
        recordAction(action);
    };

    const recordCustomisationValueDeleted = (node, val, valArray, index) => {
        const action = {
            type: ACTION_TYPES.CUSTOMISATION_VALUE_DELETED,
            payload: { node, val, valArray, index }
        };
        recordAction(action);
    };

    const recordCustomisationValueAdded = (node, val, valArray) => {
        const action = {
            type: ACTION_TYPES.CUSTOMISATION_VALUE_ADDED,
            payload: { node, val, valArray }
        };
        recordAction(action);
    };

    const recordCustomisationArrayValuesReplaced = (node, arrayObject, oldVals, newVals) => {
        const action = {
            type: ACTION_TYPES.CUSTOMISATION_VALUES_ARRAY_REPLACED,
            payload: { node, arrayObject, oldVals, newVals }
        };
        recordAction(action);
    };

    const undoLastAction = () => {
        if (undoStack.length === 0) return;
        const lastAction = undoStack[undoStack.length - 1];

        switch (lastAction.type) {
            case ACTION_TYPES.CUSTOMISATION_VALUE_CHANGE:
                const { featureId, node, dataLink, oldValue, index, visualisationComponentKey, visualisationExtraData } = lastAction.payload;
                // Revert the customisation value
                // Assume updateCustomisationValue is a function that updates the customisation value
                updateCustomisationValue(node.customisationVars, dataLink, oldValue, index, visualisationComponentKey, node, visualisationExtraData, null, false, true);
                break;
            case ACTION_TYPES.CUSTOMISATION_VALUE_DELETED:
                const { node: valDelNode, val, valArray, index: deleteIndex } = lastAction.payload;
                // Re-add the deleted value at the correct index
                valArray.splice(deleteIndex, 0, val);
                triggerSaveFeatureData(valDelNode?.featureData?._id);
                break;
            case ACTION_TYPES.CUSTOMISATION_VALUE_ADDED:
                const { node: valAddNode, val: addedVal, valArray: addedValArray } = lastAction.payload;
                // Remove the added value
                const removeIndex = addedValArray.indexOf(addedVal);
                if (removeIndex !== -1) {
                    addedValArray.splice(removeIndex, 1);
                }
                triggerSaveFeatureData(valAddNode?.featureData?._id);
                break;
            case ACTION_TYPES.CUSTOMISATION_VALUES_ARRAY_REPLACED:
                const { node: arrReplaceNode, arrayObject, oldVals, newVals } = lastAction.payload;
                // Revert to the old values
                arrayObject.splice(0, arrayObject.length, ...oldVals);
                triggerSaveFeatureData(arrReplaceNode?.featureData?._id);
                break;
            default:
                break;
        }
        setUndoStack((prevStack) => prevStack.slice(0, -1));
        setRedoStack((prevStack) => [...prevStack, lastAction]);
        onUndoRedo && onUndoRedo();
    };

    const redoLastAction = () => {
        if (redoStack.length === 0) return;
        const lastAction = redoStack[redoStack.length - 1];
        switch (lastAction.type) {
            case ACTION_TYPES.CUSTOMISATION_VALUE_CHANGE:
                const { featureId, node, dataLink, newValue, index, visualisationComponentKey, visualisationExtraData } = lastAction.payload;
                // Re-apply the customisation value
                updateCustomisationValue(node.customisationVars, dataLink, newValue, index, visualisationComponentKey, node, visualisationExtraData, null, false, true);
                break;
            case ACTION_TYPES.CUSTOMISATION_VALUE_DELETED:
                const { node: valDelNode, val, valArray, index: deleteIndex } = lastAction.payload;
                // Remove the value again
                const removeIndex = valArray.indexOf(val);
                if (removeIndex !== -1) {
                    valArray.splice(removeIndex, 1);
                }
                triggerSaveFeatureData(valDelNode?.featureData?._id);
                break;
            case ACTION_TYPES.CUSTOMISATION_VALUE_ADDED:
                const { node: valAddNode, val: addedVal, valArray: addedValArray } = lastAction.payload;
                // Re-add the value
                addedValArray.push(addedVal);
                triggerSaveFeatureData(valAddNode?.featureData?._id);
                break;
            case ACTION_TYPES.CUSTOMISATION_VALUES_ARRAY_REPLACED:
                const { node: arrReplaceNode, arrayObject, newVals } = lastAction.payload;
                // Re-apply the new values
                arrayObject.splice(0, arrayObject.length, ...newVals);
                triggerSaveFeatureData(arrReplaceNode?.featureData?._id);
                break;
            default:
                break;
        }
        setRedoStack((prevStack) => prevStack.slice(0, -1));
        setUndoStack((prevStack) => [...prevStack, lastAction]);
        onUndoRedo && onUndoRedo();
    };

    const clearUndoRedoStacks = () => {
        setUndoStack([]);
        setRedoStack([]);
    };

    return {
        undoStack,
        redoStack,
        undoLastAction,
        redoLastAction,
        clearUndoRedoStacks,
        recordAction,
        recordCustomisationValueChange,
        recordCustomisationValueDeleted,
        recordCustomisationValueAdded,
        recordCustomisationArrayValuesReplaced,
    };
}
